import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import {
  FolderOpen, Camera, ChevronRight, ChevronLeft, Check,
  AlertTriangle, X, ArrowLeft, CheckCircle, XCircle,
  RefreshCw, HardDrive
} from 'lucide-react'
import { previewPhotoFolder, scanPhotoFolder, getAllCattle } from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'

// Regex to extract RFID from filename: everything before _YYYY-MM-DD or _YYYYMMDD
const RFID_PATTERN = /^(.+?)(?:_\d{4}-?\d{2}-?\d{2}|_\d{8})/

function extractRfid(filename) {
  const stem = filename.replace(/\.[^.]+$/, '') // remove extension
  const match = stem.match(RFID_PATTERN)
  return match ? match[1] : stem
}

function StepIndicator({ currentStep }) {
  const steps = ['Scan Folder', 'Preview & Match', 'Results']
  return (
    <div className="flex items-center justify-center mb-8">
      {steps.map((label, i) => (
        <div key={label} className="flex items-center">
          <div className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${
            i < currentStep
              ? 'bg-green-100 text-green-700'
              : i === currentStep
                ? 'bg-primary-100 text-primary-700'
                : 'bg-gray-100 text-gray-500'
          }`}>
            {i < currentStep ? (
              <Check className="h-4 w-4" />
            ) : (
              <span className="h-5 w-5 rounded-full bg-current/20 flex items-center justify-center text-xs">
                {i + 1}
              </span>
            )}
            {label}
          </div>
          {i < steps.length - 1 && (
            <ChevronRight className="h-5 w-5 mx-2 text-gray-400" />
          )}
        </div>
      ))}
    </div>
  )
}

export default function PhotoSessionUpload({ ranchId }) {
  const navigate = useNavigate()
  const { userId } = useAuth()
  const [step, setStep] = useState(0)
  const [error, setError] = useState('')

  // Step 1 state
  const [folderPath, setFolderPath] = useState('')
  const [photoDate, setPhotoDate] = useState(new Date().toISOString().split('T')[0])
  const [source, setSource] = useState('crush_camera')
  const [notes, setNotes] = useState('')
  const [previewData, setPreviewData] = useState(null) // { folder_path, total_files, image_files }

  // Step 3 state
  const [scanResult, setScanResult] = useState(null)

  // Load cattle for client-side RFID matching preview
  const { data: cattleData } = useQuery({
    queryKey: ['cattle', ranchId, 'all-for-photo-match'],
    queryFn: () => getAllCattle(ranchId, { status: '' }),
    staleTime: 60000,
  })

  const cattleByRfid = useMemo(() => {
    const map = {}
    const items = cattleData || []
    for (const a of items) {
      if (a.rfid_tag) map[a.rfid_tag] = a
    }
    return map
  }, [cattleData])

  // Preview mutation — lists files in the folder
  const previewMutation = useMutation({
    mutationFn: () => previewPhotoFolder(folderPath || null),
    onSuccess: (data) => {
      setPreviewData(data)
      setStep(1)
      setError('')
    },
    onError: (err) => {
      setError(err.response?.data?.detail || 'Could not read folder')
    },
  })

  // Scan mutation — imports photos
  const scanMutation = useMutation({
    mutationFn: scanPhotoFolder,
    onSuccess: (data) => {
      setScanResult(data)
      setStep(2)
    },
    onError: (err) => {
      setError(err.response?.data?.detail || 'Scan failed')
    },
  })

  // Enrich preview files with RFID match info
  const enrichedFiles = useMemo(() => {
    if (!previewData) return []
    return previewData.image_files.map(filename => {
      const rfid = extractRfid(filename)
      const match = cattleByRfid[rfid]
      return {
        filename,
        rfid,
        matched: !!match,
        animal_name: match?.local_name || null,
        animal_category: match?.category || null,
      }
    })
  }, [previewData, cattleByRfid])

  const matchedCount = enrichedFiles.filter(f => f.matched).length
  const unmatchedCount = enrichedFiles.filter(f => !f.matched).length

  // --- Step 1: Scan Folder ---
  const handlePreview = () => {
    setError('')
    previewMutation.mutate()
  }

  // --- Step 2: Trigger import ---
  const handleScan = () => {
    setError('')
    scanMutation.mutate({
      ranch_id: ranchId,
      photo_date: photoDate,
      folder_path: folderPath || null,
      imported_by: userId,
      source,
      notes: notes || null,
    })
  }

  // --- Render ---
  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => {
            if (step === 0) navigate('/cattle')
            else setStep(step - 1)
          }}
          className="p-2 rounded-lg hover:bg-gray-100"
        >
          <ArrowLeft className="h-5 w-5 text-gray-600" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Upload Photos</h1>
          <p className="text-gray-600">Scan a folder of crush camera photos and match them to animals by RFID</p>
        </div>
      </div>

      <StepIndicator currentStep={step} />

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 flex-shrink-0" />
          <span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto"><X className="h-4 w-4" /></button>
        </div>
      )}

      {/* Step 1: Configure & Scan Folder */}
      {step === 0 && (
        <div className="bg-white rounded-lg shadow p-6 max-w-2xl mx-auto">
          <div className="space-y-6">
            {/* Info Box */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-3">
                <HardDrive className="h-5 w-5 text-blue-600 mt-0.5" />
                <div className="text-sm text-blue-700">
                  <p className="font-medium mb-1">How it works</p>
                  <ol className="list-decimal ml-4 space-y-1">
                    <li>Copy photos from the Pi SD card to the server inbox folder</li>
                    <li>Click "Scan Folder" to preview which files are found</li>
                    <li>Photos are matched to animals by RFID in the filename</li>
                    <li>Matched photos are copied to organized storage and linked to animals</li>
                  </ol>
                  <p className="mt-2 text-xs text-blue-600">
                    Expected filename format: <code className="bg-blue-100 px-1 rounded">RFID_YYYY-MM-DD_HHMMSS.jpg</code>
                  </p>
                </div>
              </div>
            </div>

            {/* Folder Path */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Folder Path
              </label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <FolderOpen className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    value={folderPath}
                    onChange={(e) => setFolderPath(e.target.value)}
                    placeholder="Leave empty to use default inbox"
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Default inbox: <code>static/photos/inbox/</code>
              </p>
            </div>

            {/* Photo Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Photo Date</label>
              <input
                type="date"
                value={photoDate}
                onChange={(e) => setPhotoDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            {/* Source */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
              <select
                value={source}
                onChange={(e) => setSource(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="crush_camera">Crush Camera (Pi)</option>
                <option value="phone">Phone</option>
                <option value="drone">Drone</option>
                <option value="other">Other</option>
              </select>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                placeholder="E.g. Weekly crush run, Herd A..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            {/* Scan Button */}
            <button
              onClick={handlePreview}
              disabled={previewMutation.isPending}
              className="w-full py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2"
            >
              {previewMutation.isPending ? (
                <>
                  <RefreshCw className="h-5 w-5 animate-spin" />
                  Scanning folder...
                </>
              ) : (
                <>
                  <FolderOpen className="h-5 w-5" />
                  Scan Folder
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Preview & Match */}
      {step === 1 && previewData && (
        <div className="space-y-6">
          {/* Folder info */}
          <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600">
            <FolderOpen className="inline h-4 w-4 mr-1" />
            <span className="font-mono">{previewData.folder_path}</span>
          </div>

          {/* Summary cards */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Photos</div>
              <div className="text-2xl font-bold">{previewData.total_files}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Matched</div>
              <div className="text-2xl font-bold text-green-600">{matchedCount}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Unmatched</div>
              <div className="text-2xl font-bold text-amber-600">{unmatchedCount}</div>
            </div>
          </div>

          {unmatchedCount > 0 && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-700 text-sm">
              <AlertTriangle className="inline h-4 w-4 mr-1" />
              {unmatchedCount} photo(s) could not be matched to an animal. Only matched photos will be imported.
            </div>
          )}

          {matchedCount === 0 && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
              <AlertTriangle className="inline h-5 w-5 mr-2" />
              No photos matched to animals. Check that filenames contain RFID tags in the format:
              <code className="ml-1 bg-red-100 px-1 rounded">RFID_YYYY-MM-DD_HHMMSS.jpg</code>
            </div>
          )}

          {/* File List Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="max-h-96 overflow-y-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Filename</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">RFID Extracted</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Animal</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {enrichedFiles.map((f, i) => (
                    <tr key={i} className={f.matched ? '' : 'bg-amber-50'}>
                      <td className="px-4 py-2">
                        {f.matched ? (
                          <CheckCircle className="h-5 w-5 text-green-500" />
                        ) : (
                          <AlertTriangle className="h-5 w-5 text-amber-500" />
                        )}
                      </td>
                      <td className="px-4 py-2 text-sm font-mono text-gray-700 truncate max-w-xs">
                        {f.filename}
                      </td>
                      <td className="px-4 py-2 text-sm font-mono text-gray-600">
                        {f.rfid}
                      </td>
                      <td className="px-4 py-2 text-sm">
                        {f.matched ? (
                          <span className="text-gray-900">
                            {f.animal_name || f.rfid}
                            {f.animal_category && (
                              <span className="ml-1 text-xs text-gray-500">({f.animal_category})</span>
                            )}
                          </span>
                        ) : (
                          <span className="text-amber-600">No match</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-between">
            <button
              onClick={() => setStep(0)}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <ChevronLeft className="h-5 w-5" />
              Back
            </button>
            <button
              onClick={handleScan}
              disabled={scanMutation.isPending || matchedCount === 0}
              className="flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {scanMutation.isPending ? (
                <>
                  <RefreshCw className="h-5 w-5 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Camera className="h-5 w-5" />
                  Import {matchedCount} Photos
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Results */}
      {step === 2 && scanResult && (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Files</div>
              <div className="text-2xl font-bold">{scanResult.total_files}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Imported</div>
              <div className="text-2xl font-bold text-green-600">{scanResult.matched}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Unmatched</div>
              <div className="text-2xl font-bold text-amber-600">{scanResult.unmatched}</div>
            </div>
          </div>

          {/* Results Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">RFID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Animal</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Message</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {scanResult.results.map((r, i) => (
                  <tr
                    key={i}
                    className={
                      r.status === 'uploaded' ? '' :
                      r.status === 'unmatched' ? 'bg-amber-50' : 'bg-red-50'
                    }
                  >
                    <td className="px-4 py-2">
                      {r.status === 'uploaded' && <CheckCircle className="h-5 w-5 text-green-500" />}
                      {r.status === 'unmatched' && <AlertTriangle className="h-5 w-5 text-amber-500" />}
                      {r.status === 'error' && <XCircle className="h-5 w-5 text-red-500" />}
                    </td>
                    <td className="px-4 py-2 text-sm font-mono text-gray-600">{r.rfid_tag}</td>
                    <td className="px-4 py-2 text-sm text-gray-900">{r.animal_name || '-'}</td>
                    <td className="px-4 py-2 text-sm text-gray-500">{r.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Navigation */}
          <div className="flex justify-center gap-4">
            <button
              onClick={() => {
                // Reset for another import
                setStep(0)
                setPreviewData(null)
                setScanResult(null)
                setFolderPath('')
                setNotes('')
              }}
              className="flex items-center gap-2 px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
            >
              <RefreshCw className="h-5 w-5" />
              Import More
            </button>
            <button
              onClick={() => navigate('/cattle')}
              className="flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium"
            >
              <ArrowLeft className="h-5 w-5" />
              Back to Cattle
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
